import { Building2, Package, User, Mail, Phone, CreditCard, Clock, Link2, Settings2, Fingerprint } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { OrganizationData } from '@/stores';
import type { BridgeEntityInfo, MatchedModule } from '@/lib/api';
import type { MigrationProperty } from '@/types';

interface SelectedModule {
  moduleKey: string;
  sourceId: string;
}

interface MigrationSummaryProps {
  organizationData: OrganizationData;
  selectedModules: SelectedModule[];
  matchedModules: MatchedModule[];
  bridgeEntity?: BridgeEntityInfo | undefined;
  modulePropertyOverrides?: Record<string, MigrationProperty[]> | undefined;
}

export function MigrationSummary({
  organizationData,
  selectedModules,
  matchedModules,
  bridgeEntity,
  modulePropertyOverrides,
}: MigrationSummaryProps) {
  const isNewOrg = organizationData.organizationType === 'New';

  return (
    <div className="space-y-6">
      {/* Bridge Entity Info */}
      {bridgeEntity && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Bridge Entity</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="min-w-0">
                <span className="text-sm text-muted-foreground">Customer Name</span>
                <p className="font-medium">{bridgeEntity.name}</p>
              </div>
              <div className="min-w-0">
                <span className="text-sm text-muted-foreground">Entity UUID</span>
                <p className="font-mono text-sm">{bridgeEntity.uuid}</p>
              </div>
              {bridgeEntity.contactEmail && (
                <div className="min-w-0">
                  <span className="text-sm text-muted-foreground">Contact Email</span>
                  <p className="font-medium break-all">{bridgeEntity.contactEmail}</p>
                </div>
              )}
              {(bridgeEntity.contactFirstName || bridgeEntity.contactLastName) && (
                <div className="min-w-0">
                  <span className="text-sm text-muted-foreground">Contact Name</span>
                  <p className="font-medium">
                    {bridgeEntity.contactFirstName} {bridgeEntity.contactLastName}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organization Info */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Organization</CardTitle>
            <Badge variant={isNewOrg ? 'default' : 'secondary'}>
              {isNewOrg ? 'New' : 'Existing'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-sm text-muted-foreground">Name</span>
              <p className="font-medium">{organizationData.organizationName}</p>
            </div>

            {organizationData.amplifyOrganizationUuid && (
              <div>
                <span className="text-sm text-muted-foreground">UUID</span>
                <p className="font-mono text-sm">{organizationData.amplifyOrganizationUuid}</p>
              </div>
            )}
          </div>

          {/* Address for new orgs */}
          {isNewOrg && organizationData.streetAddress && (
            <div>
              <span className="text-sm text-muted-foreground">Address</span>
              <p className="font-medium">
                {organizationData.streetAddress}
                {organizationData.addressLine2 && `, ${organizationData.addressLine2}`}
              </p>
              <p className="text-sm">
                {organizationData.city}, {organizationData.state} {organizationData.zipCode}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Owner Info (for new orgs) */}
      {isNewOrg && organizationData.ownerFirstName && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">Organization Owner</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Name</span>
                <p className="font-medium">
                  {organizationData.ownerFirstName} {organizationData.ownerLastName}
                </p>
              </div>

              {organizationData.ownerEmail && (
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="text-sm text-muted-foreground">Email</span>
                    <p className="font-medium">{organizationData.ownerEmail}</p>
                  </div>
                </div>
              )}

              {organizationData.ownerPhone && (
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="text-sm text-muted-foreground">Phone</span>
                    <p className="font-medium">{organizationData.ownerPhone}</p>
                  </div>
                </div>
              )}

              {organizationData.billing && (
                <div className="flex items-start gap-2">
                  <CreditCard className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="text-sm text-muted-foreground">Billing</span>
                    <p className="font-medium capitalize">{organizationData.billing}</p>
                  </div>
                </div>
              )}

              {organizationData.euid && (
                <div className="flex items-start gap-2">
                  <Fingerprint className="h-4 w-4 text-muted-foreground mt-0.5" />
                  <div>
                    <span className="text-sm text-muted-foreground">EUID</span>
                    <p className="font-medium font-mono">{organizationData.euid}</p>
                  </div>
                </div>
              )}
            </div>

            {organizationData.sendInvite && (
              <p className="text-sm text-muted-foreground mt-4">
                A welcome email will be sent to the owner.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Modules */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">
              Modules to Migrate ({selectedModules.length})
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {selectedModules.map((mod) => {
              const matched = matchedModules.find((m) => m.moduleKey === mod.moduleKey);
              const overrides = modulePropertyOverrides?.[mod.moduleKey];
              return (
                <li
                  key={mod.moduleKey}
                  className="py-2 border-b last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium">{matched?.displayName ?? mod.moduleKey}</span>
                      {matched?.productLabel && (
                        <p className="text-sm text-muted-foreground">{matched.productLabel}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="text-sm text-muted-foreground">Source ID:</span>
                      <code className="ml-2 bg-muted px-2 py-0.5 rounded text-sm">
                        {mod.sourceId}
                      </code>
                    </div>
                  </div>
                  {overrides && overrides.length > 0 && (
                    <div className="mt-2 ml-4 space-y-1">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Settings2 className="h-3 w-3" />
                        <span>Properties:</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        {overrides.map((prop) => (
                          <div key={prop.key}>
                            <span className="text-muted-foreground">{prop.label}</span>
                            <p className="font-medium">{String(prop.value)}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* Time Estimate Warning */}
      <Alert>
        <Clock className="h-4 w-4" />
        <AlertTitle>What Happens Next</AlertTitle>
        <AlertDescription>
          Each module will be provisioned on the Amplify platform, user authentication and access
          controls will be transferred, and the module interface will be updated to Amplify's
          design. Processing time varies by module. You can monitor progress in real-time.
        </AlertDescription>
      </Alert>
    </div>
  );
}
